import { useCallback, useState } from "react";
import { useSwivuBryntumContext } from "../useSwivuBryntumContext";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { generateInsight, getInsightTypeAvatar, cancelInsightGeneration } from "@/network/network";
import { Insights } from "@/bryntum-lib/models/Insights";
import {
  runIntegrationAction,
  useIntegrationErrorReporter,
  type ReportErrorFn,
} from "@/integrations";

interface GenerateInsightOptions {
  file_id?: number | null;
  insight_type: string;
  language?: string;
  signal?: AbortSignal;
  company_cam_url?: string;
  unassigned_file_id?: number;
  board_id?: number;
}

export const useFileInsights = () => {
  const { project, selectedBoard } = useSwivuBryntumContext();
  const { reportError } = useIntegrationErrorReporter();
  const [lastError, setLastError] = useState<string | null>(null);

  const clearError = useCallback(() => setLastError(null), []);

  const reportInsightError = useCallback<ReportErrorFn>(
    ({ error, fallback, surface = "toast" }) => {
      const message = reportError({ error, provider: "openai", fallback, surface });
      setLastError(message);
      return message;
    },
    [reportError]
  );

  const deleteInsight = useCallback(
    (id: string | number) => {
      if (!project) {
        console.warn("[useFileInsights] Project not available");
        return;
      }

      const store = project.getCrudStore(STORE_KEYS.insightsStore);
      if (!store) {
        console.warn(`[useFileInsights] Store with id "${STORE_KEYS.insightsStore}" not found`);
        return;
      }

      store.remove(id);
    },
    [project]
  );

  const generateInsightForFile = useCallback(
    async (file_id: number | null, insight_type: string, language: string = "en", signal?: AbortSignal, company_cam_url?: string) => {
      if (!project) {
        console.warn("[useFileInsights] Project not available");
        return;
      }

      const body: Record<string, unknown> = { insight_type, language };
      if (selectedBoard?.id) {
        body.board_id = selectedBoard.id;
      }
      if (company_cam_url) {
        body.company_cam_url = company_cam_url;
      } else {
        body.file_id = file_id;
      }

      const result = await runIntegrationAction(
        async () => {
          const response = await generateInsight(body, signal);
          const store = project.getCrudStore(STORE_KEYS.insightsStore);
          if (!store) {
            console.warn(`[useFileInsights] Store with id "${STORE_KEYS.insightsStore}" not found`);
            return response.data;
          }
          store.add(response.data);
          return response.data;
        },
        {
          provider: "openai",
          fallback: "Failed to generate insight.",
          report: reportInsightError,
        }
      );

      if (!result.ok) {
        throw new Error(result.message);
      }
    },
    [project, selectedBoard, reportInsightError]
  );

  const generateInsightForUnassignedFile = useCallback(
    async ({ unassigned_file_id, insight_type, language = "en", signal, board_id }: GenerateInsightOptions) => {
      const body: Record<string, unknown> = {
        unassigned_file_id,
        insight_type,
        language,
        board_id,
      };

      const result = await runIntegrationAction(
        async () => {
          const response = await generateInsight(body, signal);
          return response.data;
        },
        {
          provider: "openai",
          fallback: "Failed to generate insight.",
          report: reportInsightError,
        }
      );

      if (!result.ok) {
        throw new Error(result.message);
      }

      return result.data;
    },
    [reportInsightError]
  );

  const getInsightTypesAvatar = useCallback(async (insightType: string): Promise<string | null> => {
    try {
      const response = await getInsightTypeAvatar(insightType);
      return response.data?.avatar_url || null;
    } catch (error) {
      console.error(`[useFileInsights] Failed to get avatar for insight type "${insightType}":`, error);
      return null;
    }
  }, []);

  const getInsights = useCallback(
    (fileId: number | string | undefined, companyCamUrl?: string): Insights[] => {
      if (!project || (!fileId && !companyCamUrl)) {
        return [];
      }

      const store = project.getCrudStore(STORE_KEYS.insightsStore);
      if (!store) {
        console.warn(`[useFileInsights] Store with id "${STORE_KEYS.insightsStore}" not found`);
        return [];
      }

      if (companyCamUrl) {
        return store.query((insight: Insights) => insight.companyCamUrl === companyCamUrl) as Insights[];
      }

      return store.query((insight: Insights) => insight.file === fileId) as Insights[];
    },
    [project]
  );

  const cancelInsight = useCallback(
    async (file_id: number, insight_type: string, company_cam_url?: string) => {
      const body: Record<string, unknown> = { insight_type };
      if (selectedBoard?.id) {
        body.board_id = selectedBoard.id;
      }
      if (company_cam_url) {
        body.company_cam_url = company_cam_url;
      } else {
        body.file_id = file_id;
      }
      try {
        await cancelInsightGeneration(body);
      } catch (error) {
        console.error("[useFileInsights] Failed to cancel insight generation:", error);
      }
    },
    [selectedBoard]
  );

  const cancelInsightForUnassignedFile = useCallback(
    async (unassigned_file_id: number, insight_type: string) => {
      const body: Record<string, unknown> = { unassigned_file_id, insight_type };
      try {
        await cancelInsightGeneration(body);
      } catch (error) {
        console.error("[useFileInsights] Failed to cancel insight generation for unassigned file:", error);
      }
    },
    []
  );

  return {
    deleteInsight,
    generateInsight: generateInsightForFile,
    generateInsightForUnassignedFile,
    getInsightTypesAvatar,
    getInsights,
    cancelInsight,
    cancelInsightForUnassignedFile,
    reportInsightError,
    lastError,
    clearError,
  };
};
